import { CATALOG_FACET_LOCAL_STORAGE_KEY, Facet, FacetCategory, FacetItem } from '@aipb/gulliver/catalog/model';
import { selectLoading, updateLoading, withLoading } from '@aipb/shared/util/state';
import { inject, Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import {
  getEntity,
  hasEntity,
  selectAllEntities,
  selectEntity,
  setEntities,
  updateEntities,
  upsertEntitiesById,
  withEntities
} from '@ngneat/elf-entities';
import { APP_NAME } from '@aipb/gulliver/env/util';
import { excludeKeys, localStorageStrategy, persistState } from '@ngneat/elf-persist-state';

interface FacetProps {
  ui: {
    selectedTabIndex: number;
  };
}

const store = createStore(
  { name: 'catalog-facet' },
  withEntities<FacetItem>(),
  withProps<FacetProps>({
    ui: {
      selectedTabIndex: 0
    }
  }),
  withLoading()
);

@Injectable({
  providedIn: 'root'
})
export class FacetRepository {
  selectedTabIndex$ = store.pipe(select((state) => state.ui.selectedTabIndex));
  private appName = inject(APP_NAME);
  loading$ = store.pipe(selectLoading());

  facets$ = store.pipe(selectAllEntities());
  persist = persistState(store, {
    key: `${this.appName}-${CATALOG_FACET_LOCAL_STORAGE_KEY}`,
    storage: localStorageStrategy,
    source: () => store.pipe(excludeKeys(['ids', 'entities', 'loading']))
  });

  setSelectedTabIndex(selectedTabIndex: number) {
    store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        selectedTabIndex
      }
    }));
  }

  setFacets(items: FacetItem[]) {
    store.update(setEntities(items));
  }

  upsertFacet(item: FacetItem) {
    store.update(
      upsertEntitiesById(item.id, {
        updater: (entity) => ({
          ...entity,
          ...item,
          ui: {
            ...item.ui,
            ...entity.ui
          }
        }),
        creator: (id) => item
      })
    );
  }

  getEntity(id: string) {
    return store.query(getEntity(id));
  }

  hasEntity(id: string) {
    return store.query(hasEntity(id));
  }

  selectEntity(id: string) {
    return store.pipe(selectEntity(id));
  }

  setResultCount(id: string, resultCount: number) {
    store.update(
      updateEntities(id, (entity) => ({
        ...entity,
        resultCount
      }))
    );
  }

  setStatResultCount(id: string, statResultCount: number) {
    store.update(
      updateEntities(id, (entity) => ({
        ...entity,
        statResultCount
      }))
    );
  }

  setNotResultCount(id: string, notResultCount: number) {
    store.update(
      updateEntities(id, (entity) => ({
        ...entity,
        notResultCount
      }))
    );
  }

  setTreeResultCount(id: string, treeResultCount: number) {
    store.update(
      updateEntities(id, (entity) => ({
        ...entity,
        treeResultCount
      }))
    );
  }

  setTimelineResultCount(id: string, timelineResultCount: number) {
    store.update(
      updateEntities(id, (entity) => ({
        ...entity,
        timelineResultCount
      }))
    );
  }

  setMapResultCount(id: string, mapResultCount: number) {
    store.update(
      updateEntities(id, (entity) => ({
        ...entity,
        mapResultCount
      }))
    );
  }

  public toggleFacet(searchId: string, key: string, facet: Facet): void {
    store.update(
      updateEntities(searchId, (entity) => {
        return {
          ...entity,
          facets: {
            ...entity.facets,
            [key]: {
              ...entity.facets[key],
              items: entity.facets[key].items.map((item) => {
                if (item.label !== facet.label) {
                  return item;
                }

                return {
                  ...item,
                  checked: !item.checked
                };
              })
            }
          }
        };
      })
    );
  }

  public toggleFacetCategory(searchId: string, key: string): void {
    store.update(
      updateEntities(searchId, (item) => ({
        ...item,
        ui: {
          ...item.ui,
          [key]: !item.ui[key]
        }
      }))
    );
  }

  public resetFacetFilter(searchId: string): void {
    store.update(
      updateEntities(searchId, (entity) => {
        const facets: { [key: string]: FacetCategory } = {};

        for (const f of Object.keys(entity.facets)) {
          facets[f] = {
            ...entity.facets[f],
            items: entity.facets[f].items.map((d) => ({
              ...d,
              checked: false
            }))
          };
        }

        return {
          ...entity,
          facets
        };
      })
    );
  }

  setLoading(loading: boolean) {
    store.update(updateLoading(loading));
  }

  reset() {
    store.reset();
  }
}
